// ── Echo singleton ──────────────────────────────────────────
// Laravel Echo client wired to the project's Reverb server.
//
// Module-level singleton pattern (analog: `apiClient` in api-client.ts) — one
// Echo instance per browser tab. `getEcho()` lazy-constructs on first call;
// `destroyEcho()` tears it down on logout/unmount so a stale WS connection
// doesn't outlive the user session.
//
// T-06-12 mitigation (insecure WS downgrade): `forceTLS: true` (derived from
// VITE_REVERB_SCHEME) combined with `enabledTransports: ['ws', 'wss']` means
// pusher-js refuses plain `ws://` on HTTPS hosts.
//
// Auth path: `/api/broadcasting/auth` is a stateful, CSRF-protected route.
// pusher-js's auth XHR attaches the Sanctum session cookie (default
// `credentials: 'same-origin'`) but — unlike axios with `withXSRFToken` —
// does NOT mirror the XSRF-TOKEN cookie into the `X-XSRF-TOKEN` header.
// Without that header Laravel's CSRF middleware 419s the channel-auth
// request, so we read the cookie and pass it via Echo's `auth.headers`.

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

// Echo's Reverb connector reads window.Pusher under the hood. Attach early so
// any consumer that imports this module sees the global before constructing.
if (typeof window !== 'undefined') {
  window.Pusher = Pusher;
}

// `unknown` here intentionally — laravel-echo's types are generic over the
// broadcaster, and we only need a singleton holder. Public callers see the
// typed shape via `getEcho()`'s return.
let instance: Echo<'reverb'> | null = null;

// Read Laravel's XSRF-TOKEN cookie. `slice` past the first `=` (not `split`)
// so a base64 value containing `=` is not truncated; Laravel URL-encodes the
// cookie, so the value is decoded before use. Returns '' when absent (SSR /
// pre-session / jsdom) — the channel-auth request then 419s loudly rather
// than sending a malformed header.
function readXsrfToken(): string {
  if (typeof document === 'undefined') return '';
  const prefix = 'XSRF-TOKEN=';
  const row = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  return row ? decodeURIComponent(row.slice(prefix.length)) : '';
}

export function getEcho(): Echo<'reverb'> {
  if (instance) return instance;

  // VITE_* vars are baked at BUILD time. A missing VITE_REVERB_APP_KEY means
  // the frontend was built without the repo-root .env stanza (see
  // .env.example / vite.config.ts envDir). Fail with an actionable message
  // instead of pusher-js's cryptic "You must pass your app key", which
  // otherwise crashes through React at first channel subscription.
  const key = import.meta.env.VITE_REVERB_APP_KEY;
  if (!key) {
    throw new Error(
      'VITE_REVERB_APP_KEY is not set. The frontend was built without the ' +
        'Reverb env stanza — add VITE_REVERB_* to the environment .env ' +
        '(see .env.example) and rebuild the frontend (VITE_* vars are ' +
        'baked at build time, not read at runtime).',
    );
  }

  instance = new Echo<'reverb'>({
    broadcaster: 'reverb',
    key,
    wsHost: import.meta.env.VITE_REVERB_HOST,
    wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 443),
    wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 443),
    // Dedicated WS path. The Pusher protocol's default endpoint is
    // `/app/<key>`, which collides head-on with the SPA's own `/app/*`
    // route namespace — an Nginx `location /app/` can't tell a WebSocket
    // upgrade from a deep-link page refresh. Mounting the socket under its
    // own `/reverb` prefix removes the ambiguity at the architecture level:
    // `/reverb/*` is the WebSocket, `/app/*` is the SPA, with no overlap.
    // The Nginx vhost routes `/reverb/` to the Reverb upstream; see
    // deploy/nginx-reverb.conf and doc/reverb-operator.md. This path is a
    // fixed architectural constant (identical on every env), not per-env
    // config — unlike host/port/scheme it never varies.
    wsPath: '/reverb',
    forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: '/api/broadcasting/auth',
    // Session cookie travels automatically (credentials=same-origin); the
    // CSRF token must be mirrored into the header explicitly — see the note
    // at the top of this file. Read at construction time: getEcho() is called
    // post-login (token already rotated) and destroyEcho() runs on logout, so
    // the singleton's lifetime matches the session the token belongs to.
    auth: {
      headers: {
        'X-XSRF-TOKEN': readXsrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
  });
  return instance;
}

export function destroyEcho(): void {
  if (instance) {
    instance.disconnect();
    instance = null;
  }
}
